import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';

type SpeechRecognitionInstance = {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start: () => void;
  stop: () => void;
  onstart: (() => void) | null;
  onresult: ((event: { resultIndex: number; results: { length: number; [i: number]: { isFinal: boolean; 0: { transcript: string } } } }) => void) | null;
  onerror: (() => void) | null;
  onend: (() => void) | null;
};

export function useRiqueSpeech(onTranscript: (text: string) => void) {
  const [isRecording, setIsRecording] = useState(false);
  const [interim, setInterim] = useState('');
  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null);
  const onTranscriptRef = useRef(onTranscript);
  onTranscriptRef.current = onTranscript;

  const supported =
    typeof window !== 'undefined' &&
    ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window);

  useEffect(() => {
    if (!supported) return;
    const w = window as Window & {
      SpeechRecognition?: new () => SpeechRecognitionInstance;
      webkitSpeechRecognition?: new () => SpeechRecognitionInstance;
    };
    const Ctor = w.SpeechRecognition || w.webkitSpeechRecognition;
    if (!Ctor) return;
    const recognition = new Ctor();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = 'pt-BR';
    recognition.onstart = () => setIsRecording(true);
    recognition.onresult = (event: {
      resultIndex: number;
      results: { length: number; [i: number]: { isFinal: boolean; 0: { transcript: string } } };
    }) => {
      let finalText = '';
      let interimText = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const t = event.results[i][0].transcript;
        if (event.results[i].isFinal) finalText += t;
        else interimText += t;
      }
      setInterim(finalText || interimText);
      if (finalText.trim()) onTranscriptRef.current(finalText.trim());
    };
    recognition.onerror = () => {
      setIsRecording(false);
      toast.error('Erro ao capturar voz. Verifique o microfone.');
    };
    recognition.onend = () => {
      setIsRecording(false);
      setInterim('');
    };
    recognitionRef.current = recognition;
  }, [supported]);

  const start = useCallback(() => {
    if (!recognitionRef.current) {
      toast.error('Reconhecimento de voz não disponível neste navegador.');
      return;
    }
    try {
      recognitionRef.current.start();
    } catch {
      toast.error('Não foi possível iniciar o microfone.');
    }
  }, []);

  const stop = useCallback(() => {
    recognitionRef.current?.stop();
  }, []);

  const speak = useCallback((text: string) => {
    if (typeof window === 'undefined' || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const utter = new SpeechSynthesisUtterance(text);
    utter.lang = 'pt-BR';
    window.speechSynthesis.speak(utter);
  }, []);

  return { supported, isRecording, interim, start, stop, speak };
}
